import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useCenter } from "@/context/CenterContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight, MapPin, IdCard, Layers, CalendarClock, Minus, Info, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addMonths, eachDayOfInterval,
  format, isSameMonth, isSameDay,
} from "date-fns";
import { fr as frLocale, enUS } from "date-fns/locale";

const fmt = (d) => format(d, "yyyy-MM-dd");
const mondayOf = (d) => startOfWeek(d, { weekStartsOn: 1 });
const monthDays = (a) => eachDayOfInterval({ start: startOfWeek(startOfMonth(a), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(a), { weekStartsOn: 1 }) });

function MiniCalendar({ anchor, setAnchor, isSelected, isEndpoint, onPick, locale, testid }) {
  const days = monthDays(anchor);
  const dow = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div className="flex-1 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-display text-lg font-bold capitalize">{format(anchor, "MMMM yyyy", { locale })}</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setAnchor(addMonths(anchor, -1))} className="w-7 h-7 rounded-md hover:bg-accent flex items-center justify-center" data-testid={`${testid}-prev`}><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setAnchor(addMonths(anchor, 1))} className="w-7 h-7 rounded-md hover:bg-accent flex items-center justify-center" data-testid={`${testid}-next`}><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
        {dow.map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-y-1">
        {days.map((day) => {
          const sel = isSelected(day);
          const end = sel && isEndpoint(day);
          const faded = !isSameMonth(day, anchor);
          const dweek = day.getDay(); // 0 Sun ... 1 Mon
          const isMon = dweek === 1, isSun = dweek === 0;
          return (
            <button key={day.toISOString()} onClick={() => onPick(day)} data-testid={`${testid}-day-${fmt(day)}`}
              className={[
                "h-9 text-sm flex items-center justify-center transition-colors relative",
                sel && !end ? "bg-primary/15" : "",
                sel && isMon ? "rounded-l-full" : "",
                sel && isSun ? "rounded-r-full" : "",
                !sel ? "hover:bg-accent rounded-full" : "",
                faded ? "text-muted-foreground/50" : "",
              ].join(" ")}>
              {end
                ? <span className="w-9 h-9 rounded-full bg-primary text-white font-semibold flex items-center justify-center">{format(day, "d")}</span>
                : <span>{format(day, "d")}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CopyScheduleDialog({ open, onOpenChange, initialWeekStart, onDone }) {
  const { t, lang } = useT();
  const { activeCenter } = useCenter();
  const locale = lang === "fr" ? frLocale : enUS;

  const [srcAnchor, setSrcAnchor] = useState(initialWeekStart || new Date());
  const [dstAnchor, setDstAnchor] = useState(addDays(initialWeekStart || new Date(), 7));
  const [sourceWeek, setSourceWeek] = useState(mondayOf(initialWeekStart || new Date()));
  const [destWeeks, setDestWeeks] = useState(new Set());
  const [sourceCount, setSourceCount] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const ws = mondayOf(initialWeekStart || new Date());
      setSourceWeek(ws); setSrcAnchor(ws); setDstAnchor(addDays(ws, 7));
      setDestWeeks(new Set());
    }
  }, [open]); // eslint-disable-line

  const loadCount = useCallback(async (ws) => {
    try {
      const { data } = await api.get("/schedule/shifts", { params: { start: fmt(ws), end: fmt(addDays(ws, 6)) } });
      setSourceCount(data.length);
    } catch { setSourceCount(0); }
  }, []);
  useEffect(() => { if (open) loadCount(sourceWeek); }, [open, sourceWeek, loadCount]);

  const srcSel = (day) => day >= sourceWeek && day <= addDays(sourceWeek, 6);
  const srcEnd = (day) => isSameDay(day, sourceWeek) || isSameDay(day, addDays(sourceWeek, 6));
  const pickSource = (day) => setSourceWeek(mondayOf(day));

  const dstSel = (day) => destWeeks.has(fmt(mondayOf(day)));
  const dstEnd = (day) => { const m = mondayOf(day); return dstSel(day) && (isSameDay(day, m) || isSameDay(day, addDays(m, 6))); };
  const toggleDest = (day) => {
    const key = fmt(mondayOf(day));
    if (key === fmt(sourceWeek)) { toast.error(t("cannotCopyToSource")); return; }
    setDestWeeks((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const selectedList = useMemo(() => {
    return [...destWeeks].sort().map((k) => {
      const d = new Date(k + "T00:00:00");
      const weeks = Math.round((mondayOf(d) - sourceWeek) / (7 * 24 * 3600 * 1000));
      return { key: k, date: d, weeks };
    });
  }, [destWeeks, sourceWeek]);

  const removeDest = (key) => setDestWeeks((prev) => { const n = new Set(prev); n.delete(key); return n; });

  const doCopy = async () => {
    if (destWeeks.size === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post("/schedule/shifts/copy", { source_start: fmt(sourceWeek), target_starts: [...destWeeks] });
      toast.success(t("copiedShifts").replace("{n}", data.copied).replace("{w}", data.weeks));
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("copy") + " ✗"); }
    finally { setSaving(false); }
  };

  const totalToCopy = sourceCount * destWeeks.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden max-h-[92vh]" data-testid="copy-schedule-dialog">
        <div className="flex flex-col sm:flex-row max-h-[92vh]">
          {/* Left summary rail */}
          <div className="sm:w-56 shrink-0 bg-muted/40 p-5 border-b sm:border-b-0 sm:border-r border-border">
            <button onClick={() => onOpenChange(false)} className="mb-6 w-8 h-8 rounded-md hover:bg-accent flex items-center justify-center" data-testid="copy-close"><X className="w-5 h-5" /></button>
            <div className="space-y-5 text-sm">
              <div className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-muted-foreground" /><div><div className="text-xs text-muted-foreground">{t("location")}</div><div className="font-medium">{activeCenter?.name || "—"}</div></div></div>
              <div className="flex items-start gap-3"><IdCard className="w-4 h-4 mt-0.5 text-muted-foreground" /><div><div className="text-xs text-muted-foreground">{t("positionsLabel")}</div><div className="font-medium">{t("allPositions")}</div></div></div>
              <div className="flex items-start gap-3"><Layers className="w-4 h-4 mt-0.5 text-muted-foreground" /><div><div className="text-xs text-muted-foreground">{t("shiftsCount")}</div><div className="font-medium" data-testid="copy-source-count">{sourceCount}</div></div></div>
            </div>
          </div>

          {/* Main */}
          <div className="flex-1 min-w-0 overflow-y-auto p-6">
            <DialogTitle className="font-display text-3xl font-bold text-primary mb-5">{t("copy")}</DialogTitle>
            <DialogDescription className="sr-only">{t("copyInfoAll")}</DialogDescription>

            <div className="rounded-lg bg-muted/60 px-4 py-3 text-sm text-muted-foreground flex items-start gap-2 mb-3">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />{t("copyInfoAll")}
            </div>
            <div className="rounded-lg bg-amber-500/15 border border-amber-500/30 px-4 py-3 text-sm text-amber-800 flex items-start gap-2 mb-6">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{t("copyWarnTimeOff")}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="font-display text-xl font-bold mb-3">{t("source")}</h3>
                <MiniCalendar anchor={srcAnchor} setAnchor={setSrcAnchor} isSelected={srcSel} isEndpoint={srcEnd} onPick={pickSource} locale={locale} testid="src-cal" />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold mb-3">{t("destination")}</h3>
                <MiniCalendar anchor={dstAnchor} setAnchor={setDstAnchor} isSelected={dstSel} isEndpoint={dstEnd} onPick={toggleDest} locale={locale} testid="dst-cal" />
              </div>
            </div>

            {/* Selected destination weeks */}
            <div className="mt-6 space-y-2" data-testid="dest-week-list">
              {selectedList.length === 0
                ? <p className="text-sm text-muted-foreground">{t("pickDestinationWeeks")}</p>
                : selectedList.map((it) => {
                  const abs = Math.abs(it.weeks);
                  const rel = it.weeks >= 0
                    ? (abs === 1 ? t("oneWeekFromSource") : t("weeksFromSource").replace("{n}", abs))
                    : (abs === 1 ? t("oneWeekBeforeSource") : t("weeksBeforeSource").replace("{n}", abs));
                  return (
                  <div key={it.key} className="flex items-center gap-3 rounded-lg bg-muted/50 px-4 py-2.5 text-sm" data-testid={`dest-week-${it.key}`}>
                    <CalendarClock className="w-4 h-4 text-muted-foreground" />
                    <span className="font-semibold">{it.key}</span>
                    <span className="text-muted-foreground">{rel}</span>
                    <button onClick={() => removeDest(it.key)} className="ml-auto w-6 h-6 rounded-full bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center" data-testid={`remove-dest-${it.key}`}><Minus className="w-4 h-4" /></button>
                  </div>
                  );
                })}
            </div>

            <div className="mt-6 flex justify-start">
              <Button size="lg" className="rounded-full px-8" disabled={saving || destWeeks.size === 0} onClick={doCopy} data-testid="copy-submit">
                {t("copy")}
                {destWeeks.size > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-full bg-white/25 text-sm">{totalToCopy}</span>}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
